// B2 acceptance page (B01 §9.7 contract): exactly TEN shadcn/ui controls, no data binding.
//   - wrappers data-testid="control-N" data-kind="<kind>" in the order
//     button, input, checkbox, switch, select, slider, tabs, dialog, tooltip, datepicker
//   - one admin-only card (data-testid="admin-only") rendered only for role admin
//   - NO useQuery / fetch / $api in this file (ESLint no-restricted-imports + depcruise "playground-no-data"
//     + e2e/bootstrap.spec.ts assert it). All state is local React state.
// Components other than Button come from `pnpm dlx shadcn@4.20.1 add input checkbox switch select slider
// tabs dialog tooltip calendar popover` into packages/ui/src (docs/bootstrap-steps.md step 17).
import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { serverIsProduction } from "@/features/aco-shell/landing";
import { Button } from "@vbltc/ui/button";
import { Input } from "@vbltc/ui/input";
import { Checkbox } from "@vbltc/ui/checkbox";
import { Switch } from "@vbltc/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@vbltc/ui/select";
import { Slider } from "@vbltc/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@vbltc/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@vbltc/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@vbltc/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@vbltc/ui/popover";
import { Calendar } from "@vbltc/ui/calendar";
import { Combobox } from "@vbltc/ui/components/combobox/combobox";
import { DataTable, type DataTableColumn } from "@vbltc/ui/components/data-table/data-table";
import { AlertBanner } from "@vbltc/ui/components/alert-banner/alert-banner";
import {
  type ColumnConfig,
  ColumnConfigEditor,
} from "@vbltc/ui/components/column-config-editor/column-config-editor";
import { FileUpload } from "@vbltc/ui/components/file-upload/file-upload";
import { ConfigField } from "@vbltc/ui/components/structured-config-editor/config-field";
import { StructuredConfigEditor } from "@vbltc/ui/components/structured-config-editor/structured-config-editor";
import { StatusChip } from "@vbltc/ui/components/status-chip/status-chip";
import { ThresholdCell } from "@vbltc/ui/components/threshold-cell/threshold-cell";
import { Can } from "../../features/auth/can";

export const Route = createFileRoute("/_authenticated/playground")({
  // CR-005 item 4 (J-191): production has no Playground; its URL lands where sign-in does.
  beforeLoad: async () => {
    if (await serverIsProduction()) throw redirect({ to: "/" });
  },
  // The shell reads this for the page heading and the document title (22-COMPOUNDS-SHELL §2.1
  // obligations 1 and 3). Declared per route so the shell never hardcodes one screen's name.
  staticData: { screenName: "Controls" },
  component: PlaygroundPage,
});

function PlaygroundPage() {
  const [clicks, setClicks] = useState(0);
  const [text, setText] = useState("");
  const [checked, setChecked] = useState(false);
  const [on, setOn] = useState(false);
  const [bannerShown, setBannerShown] = useState(true);
  const [uploadedBytes, setUploadedBytes] = useState<number>();
  const [configName, setConfigName] = useState(PLACEHOLDER_CONFIG_NAME);
  const [configYaml, setConfigYaml] = useState(`name: ${PLACEHOLDER_CONFIG_NAME}\n`);
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { id: "name", label: "Name", visible: true },
    { id: "status", label: "Status", visible: true },
    { id: "count", label: "Count", visible: true },
  ]);
  const [option, setOption] = useState<string | undefined>(undefined);
  const [level, setLevel] = useState([40]);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [colour, setColour] = useState("");

  return (
    // No <main> and no <h1> here: the shell owns both. Two `main` landmarks is invalid HTML and
    // gives a screen-reader user two entries for one page; a second `h1` steals the focus move that
    // obligation 1 aims at the screen's heading.
    <div className="mx-auto max-w-4xl space-y-8">
      <header>
        <p className="text-muted-foreground text-sm">
          Ten placeholder controls. Nothing on this page reads or writes data.
        </p>
      </header>

      <section className="grid gap-6 sm:grid-cols-2" aria-label="Placeholder controls">
        {/* 1 Button */}
        <Field label="Button">
          <Button
            data-testid="control-1"
            data-kind="button"
            onClick={() => setClicks((c) => c + 1)}
          >
            Clicked {clicks} times
          </Button>
        </Field>

        {/* 2 Input */}
        <Field label="Input">
          <Input
            data-testid="control-2"
            data-kind="input"
            placeholder="Type anything"
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="Free text"
          />
        </Field>

        {/* 3 Checkbox */}
        <Field label="Checkbox">
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="control-3"
              data-kind="checkbox"
              id="pg-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(v === true)}
            />
            <label htmlFor="pg-checkbox" className="text-sm">
              I agree
            </label>
          </div>
        </Field>

        {/* 4 Switch */}
        <Field label="Switch">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="control-4"
              data-kind="switch"
              id="pg-switch"
              checked={on}
              onCheckedChange={setOn}
            />
            <label htmlFor="pg-switch" className="text-sm">
              Notifications {on ? "on" : "off"}
            </label>
          </div>
        </Field>

        {/* 5 Select */}
        <Field label="Select">
          {/* Spread only when set: exactOptionalPropertyTypes forbids an explicit undefined
              on Radix's optional `value` prop (same rule as the Slider). (D-035) */}
          <Select {...(option === undefined ? {} : { value: option })} onValueChange={setOption}>
            <SelectTrigger
              data-testid="control-5"
              data-kind="select"
              className="w-56"
              aria-label="Choose an option"
            >
              <SelectValue placeholder="Choose an option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="a">Option A</SelectItem>
              <SelectItem value="b">Option B</SelectItem>
              <SelectItem value="c">Option C</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        {/* 6 Slider */}
        <Field label={`Slider (${level[0] ?? 0})`}>
          <div data-testid="control-6" data-kind="slider" className="w-56 py-2">
            <Slider value={level} onValueChange={setLevel} max={100} step={1} aria-label="Level" />
          </div>
        </Field>

        {/* 7 Tabs */}
        <Field label="Tabs">
          <Tabs defaultValue="first" data-testid="control-7" data-kind="tabs" className="w-56">
            <TabsList>
              <TabsTrigger value="first">First</TabsTrigger>
              <TabsTrigger value="second">Second</TabsTrigger>
            </TabsList>
            <TabsContent value="first">First panel</TabsContent>
            <TabsContent value="second">Second panel</TabsContent>
          </Tabs>
        </Field>

        {/* 8 Dialog */}
        <Field label="Dialog">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="control-8" data-kind="dialog">
                Open dialog
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Placeholder dialog</DialogTitle>
                <DialogDescription>Press Escape to close. Nothing is saved.</DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </Field>

        {/* 9 Tooltip */}
        <Field label="Tooltip">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="secondary" data-testid="control-9" data-kind="tooltip">
                  Hover me
                </Button>
              </TooltipTrigger>
              <TooltipContent>Tooltips explain, they never store.</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </Field>

        {/* 10 Date picker (Popover + Calendar) */}
        <Field label="Date picker">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="control-10"
                data-kind="datepicker"
                className="w-56 justify-start"
              >
                {date
                  ? date.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={date} onSelect={setDate} />
            </PopoverContent>
          </Popover>
        </Field>
      </section>

      {/* The two list compounds, on static placeholder data. Outside the ten numbered controls, and
          still no data binding: CDoD-11 asks that every packages/ui component be used on a screen. */}
      <section className="grid gap-6" aria-label="List components">
        <Field label="Combobox">
          <Combobox
            name="colour"
            aria-label="Colour"
            items={PLACEHOLDER_COLOURS}
            value={colour}
            onChange={setColour}
            placeholder="Type to filter"
            emptyMessage="No colour matches."
            className="w-56"
          />
        </Field>
        <Field label="Structured configuration editor">
          {/* Placeholder: nothing is saved; an empty name shows a field-level issue. */}
          <StructuredConfigEditor
            title="Placeholder document"
            name="placeholder-document"
            yaml={configYaml}
            onYamlChange={setConfigYaml}
            fieldPaths={["name"]}
            issues={configName.trim() ? [] : [{ path: "name", message: "A name is required." }]}
            dirty={configName !== PLACEHOLDER_CONFIG_NAME}
            onSave={() => undefined}
            onDiscard={() => {
              setConfigName(PLACEHOLDER_CONFIG_NAME);
              setConfigYaml(`name: ${PLACEHOLDER_CONFIG_NAME}\n`);
            }}
            onExport={() => undefined}
          >
            <ConfigField path="name" label="Name">
              {(control) => (
                <Input
                  {...control}
                  value={configName}
                  onChange={(e) => {
                    setConfigName(e.target.value);
                    setConfigYaml(`name: ${e.target.value}\n`);
                  }}
                />
              )}
            </ConfigField>
          </StructuredConfigEditor>
        </Field>
        <Field label="Column configuration editor">
          <ColumnConfigEditor
            name="placeholder-columns"
            value={columnConfig}
            onChange={setColumnConfig}
          />
          <p className="text-muted-foreground text-sm">
            Order: {columnConfig.map((c) => `${c.label}${c.visible ? "" : " (hidden)"}`).join(", ")}
          </p>
        </Field>
        <Field label="File upload">
          {/* Placeholder: nothing is sent; the playground shows the chosen file's size only. */}
          <FileUpload
            name="placeholder-file"
            label="Choose a CSV file"
            accept=".csv"
            maxBytes={1024 * 1024}
            onFileSelected={(file) => setUploadedBytes(file.size)}
            {...(uploadedBytes === undefined ? {} : { progress: 100 })}
          />
          {uploadedBytes === undefined ? null : (
            <p className="text-muted-foreground text-sm">Chosen: {uploadedBytes} bytes.</p>
          )}
        </Field>
        <Field label="Alert banner">
          <div className="grid gap-2">
            <AlertBanner title="Placeholder information">
              Placeholder message, on screen from the first paint.
            </AlertBanner>
            {bannerShown ? (
              <AlertBanner
                variant="warning"
                title="Placeholder warning"
                onDismiss={() => setBannerShown(false)}
              >
                Placeholder message; the × button dismisses it until the page reloads.
              </AlertBanner>
            ) : null}
          </div>
        </Field>
        <Field label="Status chip">
          <div className="flex flex-wrap gap-2">
            <StatusChip variant="complete" label="Placeholder complete" />
            <StatusChip variant="pending" label="Placeholder pending" />
            <StatusChip variant="error" label="Placeholder error" />
          </div>
        </Field>
        <Field label="Threshold cell">
          <div className="flex items-center gap-4">
            <ThresholdCell value="82.4%" />
            <ThresholdCell
              value="58.0%"
              highlighted
              tooltip="Placeholder: below the completeness threshold."
            />
          </div>
        </Field>
        <Field label="Data table">
          <DataTable
            caption="Placeholder rows"
            columns={PLACEHOLDER_COLUMNS}
            rows={PLACEHOLDER_ROWS}
          />
        </Field>
      </section>

      <Can permissions={{ admin: ["ping"] }}>
        <section data-testid="admin-only" className="rounded-lg border border-dashed p-4">
          <h2 className="font-medium">Admin area</h2>
          <p className="text-muted-foreground text-sm">
            Visible to the admin role only. The matching API check is GET /api/admin/ping.
          </p>
        </section>
      </Can>
    </div>
  );
}

const PLACEHOLDER_CONFIG_NAME = "Placeholder organization";

const PLACEHOLDER_COLOURS = [
  { value: "red", label: "Red" },
  { value: "green", label: "Green" },
  { value: "blue", label: "Blue" },
];

// J-045: the column options a configured table uses — a hidden column, an end-aligned count and a
// comparator, so the table sorts the rows itself. Placeholder values only.
const PLACEHOLDER_COUNTS: Record<string, number> = { "1": 12, "2": 3, "3": 7 };

const PLACEHOLDER_COLUMNS: DataTableColumn[] = [
  { id: "name", header: "Name", sortable: true },
  { id: "status", header: "Status" },
  {
    id: "count",
    header: "Count",
    align: "end",
    sortable: true,
    compare: (a, b) => (PLACEHOLDER_COUNTS[a.id] ?? 0) - (PLACEHOLDER_COUNTS[b.id] ?? 0),
  },
  { id: "reference", header: "Reference", visible: false },
];

const PLACEHOLDER_ROWS = [
  { id: "1", cells: { name: "First item", status: "Open", count: "12", reference: "ref-1" } },
  { id: "2", cells: { name: "Second item", status: "Closed", count: "3", reference: "ref-2" } },
  { id: "3", cells: { name: "Third item", status: "Open", count: "7", reference: "ref-3" } },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2 rounded-lg border p-4">
      <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{label}</p>
      {children}
    </div>
  );
}
